import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '@/lib/api';
import { Button, Checkbox, ErrorState, Field, Input, LoadingBlock } from '@/components/ui';
import { copy, useI18n } from '@/lib/i18n';

export default function Invitation() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [info, setInfo] = useState<{ email: string; accountName: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', passwordConfirm: '', kvkkRead: false, termsAccepted: false });

  useEffect(() => {
    if (!token) { setError(c('Davet bağlantısı eksik.', 'Invitation link is incomplete.')); setLoading(false); return; }
    api.get<{ email: string; accountName: string; role: string }>(`/auth/invitations/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((reason) => setError(reason instanceof Error ? reason.message : c('Davet alınamadı.', 'Could not load invitation.')))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (form.password !== form.passwordConfirm) { setError(c('Parolalar eşleşmiyor.', 'Passwords do not match.')); return; }
    setBusy(true); setError(null);
    try {
      await api.post(`/auth/invitations/${encodeURIComponent(token)}/accept`, form);
      setDone(true);
    } catch (reason) {
      setError(reason instanceof ApiRequestError ? reason.message : c('Davet kabul edilemedi.', 'Could not accept invitation.'));
    } finally { setBusy(false); }
  }

  if (loading) return <LoadingBlock rows={4} label={c('Davet doğrulanıyor', 'Verifying invitation')} />;
  if (!info || (error && !form.name)) return <ErrorState message={error ?? c('Davet bulunamadı.', 'Invitation not found.')} />;
  if (done) return <><h1 className="auth__title">{c('Ekibe katıldınız', 'You joined the team')}</h1><p className="auth__sub">{info.accountName}</p><Link className="btn btn--primary btn--lg auth__wide-action" to="/giris">{c('Giriş yap', 'Sign in')}</Link></>;

  return <>
    <div><h1 className="auth__title">{c(`${info.accountName} ekibine katılın`, `Join the ${info.accountName} team`)}</h1><p className="auth__sub">{info.email}</p></div>
    <form className="auth__form" onSubmit={submit}>
      {error && <div className="auth__error" role="alert">{error}</div>}
      <Field label={c('Ad Soyad', 'Full name')} required>{({ id }) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />}</Field>
      <div className="auth__form-grid auth__form-grid--passwords"><Field label={c('Parola', 'Password')} required>{({ id }) => <Input id={id} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />}</Field><Field label={c('Parola tekrarı', 'Confirm password')} required>{({ id }) => <Input id={id} type="password" value={form.passwordConfirm} onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })} autoComplete="new-password" />}</Field></div>
      <div className="auth__legal-checks"><Checkbox checked={form.kvkkRead} onChange={(e) => setForm({ ...form, kvkkRead: e.target.checked })} label={<Link to="/kvkk-aydinlatma" target="_blank">{c('KVKK Aydınlatma Metni’ni okudum.', 'I have read the Privacy Notice.')}</Link>} /><Checkbox checked={form.termsAccepted} onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })} label={<Link to="/kullanim-kosullari" target="_blank">{c('Kullanım Koşulları’nı kabul ediyorum.', 'I accept the Terms of Use.')}</Link>} /></div>
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!form.kvkkRead || !form.termsAccepted}>{c('Daveti kabul et', 'Accept invitation')}</Button>
    </form>
  </>;
}
